import type { StatusBarStyle } from "@capacitor/core";
import { Capacitor } from "@capacitor/core";
import { colors } from "lib/theme";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";
import { IS_IN_APP_BROWSER_SEARCH_PARAM } from ".";

export const useAppActive = () => {
  const [active, setActive] = useState(true);
  useEffect(() => {
    if (!Capacitor.isPluginAvailable("App")) {
      return;
    }
    const listener = Capacitor.Plugins.App.addListener("appStateChange", (s) =>
      setActive(s.isActive)
    );
    return listener.remove;
  }, []);
  return active;
};

export const useHideSplashScreen = (enabled = true) =>
  useEffect(() => {
    if (enabled) {
      void Capacitor.Plugins.SplashScreen.hide();

      if (Capacitor.isPluginAvailable("IntercomPlugin")) {
        void Capacitor.Plugins.IntercomPlugin.displayInAppMessages();
      }
    }
  }, [enabled]);

// The built in Splash class removes itself when the app is backgrounded
// The ideal solution is to recreate it with a Splash class + plugin that we control
// For now let's reshow when the app becomes active again
export const useShowSplashScreen = () => {
  const appActive = useAppActive();
  useEffect(() => {
    if (appActive) {
      // TODO: should fadeInDuration be 0?
      void Capacitor.Plugins.SplashScreen.show({ autoHide: false });

      if (Capacitor.isPluginAvailable("IntercomPlugin")) {
        void Capacitor.Plugins.IntercomPlugin.hideInAppMessages();
      }
    }
  }, [appActive]);
};

const setAccessoryBar = async (isVisible) => {
  if (Capacitor.platform !== "ios") {
    return;
  }
  try {
    await Capacitor.Plugins.Keyboard.setAccessoryBarVisible({ isVisible });
  } catch (e) {
    console.warn("Failed to set accessory bar", e);
  }
};

/**
 * Intercepts onFocus and onBlur calls to globally enable the form accessory bar in the iOS app
 */
export const useAccessoryBarFocusProps = ({ onTouchStart, onBlur }) => {
  useEffect(() => {
    // reset on unmount
    return () => setAccessoryBar(false);
  }, []);

  return useMemo(
    () => ({
      onTouchStart: (e) => {
        void setAccessoryBar(true);
        return onTouchStart?.(e);
      },
      onBlur: (e) => {
        const targetIsSelect = e.relatedTarget?.tagName === "SELECT";
        if (!targetIsSelect) {
          void setAccessoryBar(false);
        }
        return onBlur?.(e);
      },
    }),
    [onTouchStart, onBlur]
  );
};

/**
 * Asks for notification permissions when the user shows intent to have an intercom conversation
 */
export const useRegisterNotifications = () => {
  useEffect(() => {
    if (!Capacitor.isPluginAvailable("IntercomPlugin")) {
      return;
    }

    void Capacitor.Plugins.IntercomPlugin.addListener("newConversation", () => {
      void Capacitor.Plugins.PushNotifications.requestPermission();
    });
  }, []);
};

export const useIsInAppBrowser = () => {
  return useMemo(() => {
    if (!process.browser) {
      return false;
    }
    const url = new URL(window.location.href);
    return url.searchParams.has(IS_IN_APP_BROWSER_SEARCH_PARAM);
  }, []);
};

// A globally debounced function to prevent flicker when changing between dark pages
const setStatusBar = debounce((style: StatusBarStyle) => {
  void Capacitor.Plugins.StatusBar.setStyle({ style });

  // TODO: Use setOverlaysWebView when we have more consistent headers
  if (Capacitor.getPlatform() === "android") {
    void Capacitor.Plugins.StatusBar.setBackgroundColor({
      color: style === "DARK" ? colors.navy[500] : colors.gray[50],
    });
  }
}, 0);

export const useDarkStatusBar = (isDark = true) =>
  useEffect(() => {
    if (Capacitor.isPluginAvailable("StatusBar") && isDark) {
      setStatusBar("DARK" as StatusBarStyle.Dark);

      return () => setStatusBar("LIGHT" as StatusBarStyle.Dark);
    }
  }, [isDark]);
